import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Form, Button, Spinner } from "react-bootstrap";
import { supabase } from "../../config/Supabase";

const PAGE_SIZE = 25;

const STATUS_VARIANTS = {
  trial_pending: "secondary",
  pending_payout: "warning",
  paid: "success",
  voided: "danger",
};

const STATUS_LABELS = {
  trial_pending: "Trial pending",
  pending_payout: "Pending payout",
  paid: "Paid",
  voided: "Voided",
};

const formatMoney = (cents, currency = "USD") => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
};

const ConversionsTable = ({ affiliateId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");

  useEffect(() => {
    if (!affiliateId) return;

    let cancelled = false;
    const fetchRows = async () => {
      setLoading(true);
      let query = supabase
        .from("affiliate_conversions")
        .select("*", { count: "exact" })
        .eq("affiliate_id", affiliateId)
        .neq("commission_status", "not_commissionable")
        .order("occurred_at", { ascending: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      if (statusFilter) query = query.eq("commission_status", statusFilter);
      if (eventFilter) query = query.eq("event_type", eventFilter);

      const { data, error, count: total } = await query;
      if (cancelled) return;

      if (error) {
        console.error("Failed to load conversions:", error);
        setRows([]);
        setCount(0);
      } else {
        setRows(data || []);
        setCount(total || 0);
      }
      setLoading(false);
    };

    fetchRows();
    return () => {
      cancelled = true;
    };
  }, [affiliateId, page, statusFilter, eventFilter]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <Card className="shadow-sm" style={{ border: "none" }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 style={{ margin: 0 }}>Conversions</h5>
          <div className="d-flex gap-2 flex-wrap">
            <Form.Select
              size="sm"
              value={eventFilter}
              onChange={(e) => {
                setEventFilter(e.target.value);
                setPage(0);
              }}
              style={{ width: "180px", maxWidth: "100%" }}
            >
              <option value="">All event types</option>
              <option value="TRIAL_STARTED">Trial started</option>
              <option value="INITIAL_PURCHASE">Initial purchase</option>
              <option value="CANCELLATION">Cancellation</option>
              <option value="EXPIRATION">Expiration</option>
              <option value="REFUND">Refund</option>
            </Form.Select>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              style={{ width: "170px", maxWidth: "100%" }}
            >
              <option value="">All statuses</option>
              <option value="trial_pending">Trial pending</option>
              <option value="pending_payout">Pending payout</option>
              <option value="paid">Paid</option>
              <option value="voided">Voided</option>
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : rows.length === 0 ? (
          <p style={{ color: "#888", textAlign: "center", margin: "24px 0" }}>
            No conversions yet. Share your promo code to get started.
          </p>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th>Product</th>
                    <th>Store</th>
                    <th>Net</th>
                    <th>Commission</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {new Date(row.occurred_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td>{row.event_type}</td>
                      <td>{row.product_id || "—"}</td>
                      <td>{row.store || "—"}</td>
                      <td>{formatMoney(row.net_amount_cents, row.currency)}</td>
                      <td>
                        {formatMoney(
                          row.commission_amount_cents,
                          row.currency
                        )}
                      </td>
                      <td>
                        <Badge
                          bg={STATUS_VARIANTS[row.commission_status] || "secondary"}
                        >
                          {STATUS_LABELS[row.commission_status] ||
                            row.commission_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
              <span style={{ fontSize: "13px", color: "#888" }}>
                Page {page + 1} of {totalPages} ({count} total)
              </span>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ConversionsTable;
